import { FC, ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  DataRoomActivityDetail,
  DataRoomActivityWindow,
  DataRoomStatus,
  DEFAULT_DATA_ROOM_ACTIVITY_WINDOW,
} from 'shared';
import { twJoin } from 'tailwind-merge';

import { ChevronLeftIcon, HelpCircle, WorkspaceFolderIcon } from '../../../../assets/icons/index';
import avgSessionIcon from '../../../../assets/images/data-room-activity/avg-session.svg';
import dataRoomAvatarIcon from '../../../../assets/images/data-room-activity/data-room-avatar.svg';
import roomFilesIcon from '../../../../assets/images/data-room-activity/room-files.svg';
import roomTotalDownloadsIcon from '../../../../assets/images/data-room-activity/room-total-downloads.svg';
import roomTotalViewsIcon from '../../../../assets/images/data-room-activity/room-total-views.svg';
import roomUniqueVisitorsIcon from '../../../../assets/images/data-room-activity/room-unique-visitors.svg';
import totalViewingTimeIcon from '../../../../assets/images/data-room-activity/total-viewing-time.svg';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { StaggerFadeIn, StaggerItem } from '../../../../components/motion/index';
import { Table, TableRow } from '../../../../components/table/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { StringKey } from '../../../../lang/index';
import { PageRoute } from '../../../../types/page.types';
import { useSpacesHome } from '../../hooks/use-spaces-home.hook';
import { SidebarTreeStore } from '../../store/sidebar-tree.store';
import { toSelectedSpace } from '../../utils/space-list-item.util';
import { BrowserFileIcon } from '../../workspaces/browser/browser-file-icon';
import { ActivityLoadError } from '../components/activity-load-error';
import { ActivityRoomPlaceholder } from '../components/activity-loading-placeholders';
import {
  durationParts,
  MetricCard,
  MetricIcon,
  MetricPanel,
} from '../components/activity-metric-cards';
import { ActivitySection } from '../components/activity-section';
import { ActivityWindowToggle } from '../components/activity-window-toggle';
import { EngagementPanel } from '../components/engagement-chart';
import { LinkArrow } from '../components/link-arrow';
import { RoomVisitorTable } from '../components/room-visitor-table';
import { useActivityRoomDetail } from '../hooks/use-activity-room-detail.hook';
import { ActivityOutletContext } from '../types/activity-outlet-context.types';

enum Column {
  DOWNLOADS = 'downloads',
  FILE = 'file',
  FILES = 'files',
  FOLDER = 'folder',
  VIEWS = 'views',
  VISITORS = 'visitors',
}

// §9 and §10, in the order the story lists them.
const FILE_COLUMNS: Exclude<Column, Column.FILES>[] = [
  Column.FILE,
  Column.FOLDER,
  Column.VIEWS,
  Column.VISITORS,
  Column.DOWNLOADS,
];
const FOLDER_COLUMNS: Exclude<Column, Column.FILE>[] = [
  Column.FOLDER,
  Column.VIEWS,
  Column.VISITORS,
  Column.FILES,
  Column.DOWNLOADS,
];

const COLUMN_TITLE: Record<Column, StringKey> = {
  [Column.DOWNLOADS]: 'DOWNLOADS',
  [Column.FILE]: 'FILE',
  [Column.FILES]: 'FILES',
  [Column.FOLDER]: 'FOLDER',
  [Column.VIEWS]: 'VIEWS',
  [Column.VISITORS]: 'VISITORS',
};

const columnsTitle = <T extends Column>(columns: T[]) =>
  Object.fromEntries(
    columns.map((column) => [
      column,
      <AppFormattedMessage id={COLUMN_TITLE[column]} key={column} />,
    ]),
  );

const NameCell: FC<{ icon: ReactNode; name: string }> = ({ icon, name }) => (
  <span className="flex min-w-0 items-center gap-3">
    {icon}
    <span className="truncate" title={name}>
      {name}
    </span>
  </span>
);

const EmptyTable: FC = () => (
  <span className="px-4 py-6 text-sm font-[450] text-gray-400">
    <AppFormattedMessage id="NO_ACTIVITY_YET" />
  </span>
);

const RoomHeader: FC<{ detail: DataRoomActivityDetail }> = ({ detail }) => {
  const navigate = useNavigate();
  const { home } = useSpacesHome();
  const reveal = SidebarTreeStore((state) => state.reveal);
  const room = home?.dataRooms.find(({ id }) => id === detail.dataRoomId);

  // Opens this room in Workspaces, the way its card on the Workspaces home does; until the home
  // list has it, the link lands on that home instead.
  const viewDataRoom = (): void => {
    if (room) reveal({ breadcrumbs: [], folderId: null, space: toSelectedSpace(room) });
    navigate(PageRoute.DATA_ROOM_WORKSPACES);
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex min-w-0 items-center gap-2">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-white shadow-sm">
          <img alt="" className="size-8" src={dataRoomAvatarIcon} />
        </span>
        <div className="flex min-w-0 flex-col">
          <h2 className="truncate text-xl font-semibold text-[#181D27]">{detail.name}</h2>
          <span
            className={twJoin(
              'text-label-md font-medium',
              detail.status === DataRoomStatus.ACTIVE ? 'text-forest-600' : 'text-gray-500',
            )}
          >
            <AppFormattedMessage
              id={detail.status === DataRoomStatus.ACTIVE ? 'ACTIVE' : 'INACTIVE'}
            />
          </span>
        </div>
      </div>
      <button
        className="group flex shrink-0 items-center gap-2 text-sm font-medium leading-4 text-brand-700"
        onClick={viewDataRoom}
        type="button"
      >
        <AppFormattedMessage id="VIEW_DATA_ROOM" />
        <LinkArrow />
      </button>
    </div>
  );
};

const RoomContent: FC<{ detail: DataRoomActivityDetail; isReloading: boolean }> = ({
  detail,
  isReloading,
}) => {
  const { totals } = detail;

  return (
    <div className="p-4">
      {/* The sections rise in one after another on arrival; a new window dims them while it loads. */}
      <StaggerFadeIn
        className={twJoin(
          'flex flex-col gap-4 rounded-lg bg-white p-4 shadow-sm transition-opacity duration-200',
          isReloading && 'opacity-60',
        )}
      >
        <StaggerItem>
          {/* §5 and §7: Files and Folders are current state; the other five are the window's. */}
          <MetricPanel header={<RoomHeader detail={detail} />}>
            <MetricCard
              icon={<MetricIcon src={roomUniqueVisitorsIcon} />}
              label="UNIQUE_VISITORS"
              parts={[{ value: totals.uniqueVisitors }]}
            />
            <MetricCard
              icon={<MetricIcon src={roomTotalViewsIcon} />}
              label="TOTAL_VIEWS"
              parts={[{ value: totals.views }]}
            />
            <MetricCard
              icon={<MetricIcon src={roomTotalDownloadsIcon} />}
              label="TOTAL_DOWNLOADS"
              parts={[{ value: totals.downloads }]}
            />
            <MetricCard
              icon={<MetricIcon src={roomFilesIcon} />}
              label="FILES"
              parts={[{ value: detail.files }]}
            />
            <MetricCard
              icon={<WorkspaceFolderIcon />}
              label="FOLDERS"
              parts={[{ value: detail.folders }]}
            />
            <MetricCard
              icon={<MetricIcon src={totalViewingTimeIcon} />}
              label="TOTAL_VIEWING_TIME"
              parts={durationParts(totals.viewingTimeMs)}
            />
            <MetricCard
              icon={<MetricIcon src={avgSessionIcon} />}
              label="AVG_SESSION"
              parts={durationParts(totals.avgSessionMs)}
            />
          </MetricPanel>
        </StaggerItem>

        {/* §8 */}
        <StaggerItem>
          <EngagementPanel engagement={detail.engagement} />
        </StaggerItem>

        {/* Side by side while both tables keep room for a name; stacked once they would not. */}
        <StaggerItem className="flex flex-wrap items-stretch gap-4">
          {/* §9 */}
          <ActivitySection className="min-w-[440px]" title="MOST_POPULAR_FILES">
            {detail.popularFiles.length ? (
              <div className="overflow-hidden rounded-md border border-gray-100">
                <Table
                  className="table-fixed [&_tbody]:divide-gray-100"
                  columnOrder={FILE_COLUMNS}
                  columnsTitle={columnsTitle(FILE_COLUMNS)}
                  tHeadClassNames={{
                    thClassName: {
                      [Column.DOWNLOADS]: 'w-20',
                      [Column.FOLDER]: 'w-[90px]',
                      [Column.VIEWS]: 'w-[51px]',
                      [Column.VISITORS]: 'w-[90px]',
                    },
                  }}
                >
                  {detail.popularFiles.map((file) => (
                    <TableRow
                      columnOrder={FILE_COLUMNS}
                      contentClassName="gap-3"
                      fields={{
                        id: file.fileId,
                        [Column.FILE]: (
                          <NameCell
                            icon={<BrowserFileIcon fileName={file.name} size={16} />}
                            name={file.name}
                          />
                        ),
                        [Column.FOLDER]: (
                          <span className="block truncate" title={file.folderName}>
                            {file.folderName}
                          </span>
                        ),
                        [Column.VIEWS]: file.views,
                        [Column.VISITORS]: file.visitors,
                        [Column.DOWNLOADS]: file.downloads,
                      }}
                      key={file.fileId}
                      labelClassName={{ general: 'min-w-0 text-sm' }}
                      tdClassName={{
                        general: 'p-0',
                        [Column.FILE]: 'min-w-0',
                        [Column.FOLDER]: 'min-w-0 max-w-[90px]',
                      }}
                    />
                  ))}
                </Table>
              </div>
            ) : (
              <EmptyTable />
            )}
          </ActivitySection>

          {/* §10 */}
          <ActivitySection
            actions={
              <Tooltip content={<AppFormattedMessage id="FOLDER_ANALYTICS_HINT" />}>
                <HelpCircle height={16} iconColor="#000000" width={16} />
              </Tooltip>
            }
            className="min-w-[440px]"
            title="FOLDER_ANALYTICS"
          >
            {detail.folderAnalytics.length ? (
              <div className="overflow-hidden rounded-md border border-gray-100">
                <Table
                  className="table-fixed [&_tbody]:divide-gray-100"
                  columnOrder={FOLDER_COLUMNS}
                  columnsTitle={columnsTitle(FOLDER_COLUMNS)}
                  tHeadClassNames={{
                    thClassName: {
                      [Column.DOWNLOADS]: 'w-20',
                      [Column.FILES]: 'w-[90px]',
                      [Column.VIEWS]: 'w-[51px]',
                      [Column.VISITORS]: 'w-[60px]',
                    },
                  }}
                >
                  {detail.folderAnalytics.map((folder) => (
                    <TableRow
                      columnOrder={FOLDER_COLUMNS}
                      contentClassName="gap-3"
                      fields={{
                        id: folder.folderId,
                        [Column.FOLDER]: (
                          <NameCell
                            icon={<WorkspaceFolderIcon className="size-4" height={16} width={16} />}
                            name={folder.name}
                          />
                        ),
                        [Column.VIEWS]: folder.views,
                        [Column.VISITORS]: folder.visitors,
                        [Column.FILES]: folder.files,
                        [Column.DOWNLOADS]: folder.downloads,
                      }}
                      key={folder.folderId}
                      labelClassName={{ general: 'min-w-0 text-sm' }}
                      tdClassName={{ general: 'p-0', [Column.FOLDER]: 'min-w-0' }}
                    />
                  ))}
                </Table>
              </div>
            ) : (
              <EmptyTable />
            )}
          </ActivitySection>
        </StaggerItem>

        {/* §11 */}
        <StaggerItem>
          <RoomVisitorTable total={detail.visitorCount} visitors={detail.visitors} />
        </StaggerItem>
      </StaggerFadeIn>
    </div>
  );
};

/**
 * One data room's Activity detail — 20.1.2 §5 to §11.
 *
 * Every number here is read, never computed: the window metrics, the subfolder rollup and the
 * per-room visitor rows all come from the one read layer that defines them, so this page cannot
 * disagree with the list it was opened from.
 */
export const ActivityRoomDetailTab: FC = () => {
  const navigate = useNavigate();
  const { dataRoomId = '' } = useParams<{ dataRoomId: string }>();
  const { toolbar } = useOutletContext<ActivityOutletContext>();
  // §6: this screen's window, independent of the list's.
  const [window, setWindow] = useState<DataRoomActivityWindow>(DEFAULT_DATA_ROOM_ACTIVITY_WINDOW);
  const { detail, isError, isPending, isReloading, retry } = useActivityRoomDetail({
    dataRoomId,
    window,
  });

  const renderContent = () => {
    if (isPending) {
      return <ActivityRoomPlaceholder />;
    }

    if (isError || !detail) {
      return <ActivityLoadError onRetry={() => void retry()} />;
    }

    return <RoomContent detail={detail} isReloading={isReloading} />;
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-y-auto">
      {/* Drawn beside the sub-tab buttons, but the window it sets stays this screen's own. */}
      {toolbar &&
        createPortal(<ActivityWindowToggle onChange={setWindow} value={window} />, toolbar)}
      <div className="px-4 pt-4">
        <button
          className="flex items-center gap-2 text-sm font-medium leading-4 text-gray-700"
          onClick={() => navigate(PageRoute.DATA_ROOM_ACTIVITY_DATA_ROOMS)}
          type="button"
        >
          <ChevronLeftIcon className="size-4" />
          <AppFormattedMessage id="ALL_DATA_ROOMS" />
        </button>
      </div>
      {renderContent()}
    </div>
  );
};
